import React, { useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  FormGroup,
  Label,
  Input,
} from 'reactstrap';
import { useGetEventAccountStockLazyQuery } from '../../../graphql/queries/getEventAccountStock.generated';
import { LayoutStoreInstance } from '../../../LayoutStore';
import { getDepositClearingCsvBlob } from '../EventUtils/DepositClearingReport';
import i18n, { DateFormatOptionsForFileNames } from '@/utils/i18n';
import { download, sanitizeFilename } from '@/utils/download';
import { AllEventsFragmentFragment } from '../../../graphql/fragments/allEventsFragement.generated';
import {
  EventAccount,
  EventAccountStock,
  EventStock,
} from '../../../graphql/generatedTypes';
import { useGetEventStockLazyQuery } from '../../../graphql/queries/getEventStock.generated';
import { generateInvoiceInfoReport } from '../EventUtils/InvoiceInfoReport';
import DateTimePicker from '../../../components/DateTimePicker';
import { FaDownload, FaFileCsv, FaFilePdf } from 'react-icons/fa';
import { useExportEventAccountOrderFulfillmentsLazyQuery } from '../../../graphql/queries/exportEventAccountOrderFulfillments.generated';
import { useGetAggregatedEventMerchantOrderDataByTypeLazyQuery } from '../../../graphql/queries/getAggregatedEventMerchantOrderDataByType.generated';
import { useGetMerchantOrderSharedLinksForEventExportLazyQuery } from '../../../graphql/queries/getMerchantOrderSharedLinksForEventExport.generated';
import { toCsvBlob } from '@/utils/data';
import DownloadAuthorizedSignerPdf, {
  AUTHORIZED_SIGNER_QR_CODE_PREFIX,
} from '../AuthorizedSigners/AuthorizedSignerPdf';
import { getPresetKeyForStatus } from '../EventOrder/components/orderStatusConfig';

type ReportType = 'deposit_clearing' | 'invoice_info';

type DownloadsModalProps = {
  event: AllEventsFragmentFragment;
  eventAccount?: EventAccount;
  showDeposit: boolean;
  showInvoice: boolean;
  showOrderfulfillment: boolean;
  showEventOpsOrders: boolean;
  showAllEventOpsOrders: boolean;
  showAuthorizedSigner: boolean;
};

const DownloadsModal = ({
  event,
  eventAccount,
  showDeposit = false,
  showInvoice = false,
  showOrderfulfillment = false,
  showEventOpsOrders = false,
  showAllEventOpsOrders = false,
  showAuthorizedSigner = false,
}: DownloadsModalProps) => {
  const [loadingDepositClearing, setLoadingDepositClearing] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [loadingOrderfulfillment, setLoadingOrderfulfillment] = useState(false);
  const [loadingEventOpsOrders, setLoadingEventOpsOrders] = useState(false);
  const [loadingAllEventOpsOrders, setLoadingAllEventOpsOrders] =
    useState(false);
  const [loadingAuthorizedSignerCSV, setLoadingAuthorizedSignerCSV] =
    useState(false);
  const [loadingAuthorizedSignerPDF, setLoadingAuthorizedSignerPDF] =
    useState(false);
  const [loadingMultiAuthorizedSignerPDF, setLoadingMultiAuthorizedSignerPDF] =
    useState(false);
  const [isInvoiceDownloadModalOpen, setIsInvoiceDownloadModalOpen] =
    useState(false);

  const [reportType, setReportType] = useState<ReportType | null>(null);
  const [eventInfo, setEventInfo] = useState<{
    eventId: string;
    eventName: string;
  } | null>(null);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEventEndDate] = useState<Date | null>(null);
  const [
    filterUnusedContainersAndItemLocations,
    setFilterUnusedContainersAndItemLocations,
  ] = useState(false);

  const [exportOrderFulfillments] =
    useExportEventAccountOrderFulfillmentsLazyQuery({
      fetchPolicy: 'no-cache',
    });
  const [queryBillingEvent] = useGetEventStockLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [queryBillingEventAccount] = useGetEventAccountStockLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [queryEventOpsOrders] =
    useGetAggregatedEventMerchantOrderDataByTypeLazyQuery({
      fetchPolicy: 'no-cache',
    });
  const [queryAllEventOpsOrders] =
    useGetMerchantOrderSharedLinksForEventExportLazyQuery({
      fetchPolicy: 'no-cache',
    });

  function openDateRangePickerModal(
    eventId: string,
    eventName: string,
    reportType: ReportType,
  ) {
    setReportType(reportType);
    setEventInfo({ eventId, eventName });
    setIsInvoiceDownloadModalOpen(true);
  }

  function closeDateRangePickerModal() {
    setEventInfo(null);
    setEventEndDate(null);
    setStartDate(null);
    setReportType(null);
    setIsInvoiceDownloadModalOpen(false);
  }

  async function onDateRangePickerDownloadClick(
    startDate: Date | null,
    endDate: Date | null,
  ) {
    if (reportType === 'deposit_clearing') {
      await startDownloadDepositClearing(startDate, endDate);
    }

    if (reportType === 'invoice_info') {
      await downloadInvoiceInfo(startDate, endDate);
    }
  }

  const startDownloadDepositClearing = async (
    startDate: Date | null,
    endDate: Date | null,
  ) => {
    setLoadingDepositClearing(true);

    try {
      await downloadDepositClearing({
        eventId: eventInfo?.eventId || '',
        eventName: eventInfo?.eventName || '',
        eventAccountId: eventAccount?.id,
        startDate,
        endDate,
      });
    } catch (e: any) {
      alert('Error generating deposit clearing: ' + e.message);
    }

    closeDateRangePickerModal();
    setLoadingDepositClearing(false);
  };

  async function downloadDepositClearing({
    eventId,
    eventAccountId,
    eventName,
    startDate,
    endDate,
  }: {
    eventId: string;
    eventAccountId?: string;
    eventName: string;
    startDate: Date | null;
    endDate: Date | null;
  }) {
    if (eventId == null || eventAccountId == null) {
      LayoutStoreInstance.setError(
        'Can not get deposit clearing info without event id and event account id',
      );
      return;
    }

    const res = await queryBillingEventAccount({
      variables: {
        event_account_id: eventAccountId,
        event_id: eventId,
        start_date: startDate?.toISOString(),
        end_date: endDate?.toISOString(),
      },
    });

    if (res.data?.getEventAccountStock == null) {
      LayoutStoreInstance.setError('Error getting deposit clearing info');
      return;
    }

    const blob = await getDepositClearingCsvBlob(
      res.data?.getEventAccountStock,
    );

    const date = new Date()
      .toLocaleDateString(i18n.language, DateFormatOptionsForFileNames)
      .replaceAll('/', '-');

    const fileName = sanitizeFilename(
      `pfand_clearing_${eventAccount?.name}_${eventName}_${eventId}_${date}.csv`,
    );

    download(blob, fileName);
  }

  async function downloadInvoiceInfo(
    startDate: Date | null,
    endDate: Date | null,
  ) {
    setLoadingInvoice(true);

    try {
      await handleInvoiceInfoDownload({
        eventId: eventInfo?.eventId || '',
        eventName: eventInfo?.eventName || '',
        eventAccountId: eventAccount?.id || undefined,
        startDate,
        endDate,
      });
    } catch (e: any) {
      alert('Error generating invoice info: ' + e.message);
    }

    closeDateRangePickerModal();
    setLoadingInvoice(false);
  }

  async function handleInvoiceInfoDownload({
    eventId,
    eventName,
    eventAccountId,
    startDate,
    endDate,
  }: {
    eventId: string;
    eventName: string;
    eventAccountId?: string;
    startDate: Date | null;
    endDate: Date | null;
  }) {
    let stock = {};

    if (eventAccountId) {
      const res = await queryBillingEventAccount({
        variables: {
          event_id: eventId,
          event_account_id: eventAccountId,
          start_date: startDate?.toISOString(),
          end_date: endDate?.toISOString(),
        },
      });

      if (res.data?.getEventAccountStock == null) {
        LayoutStoreInstance.setError(i18n.t('Events:errorGettingInvoiceInfo'));
        return;
      }

      stock = res.data?.getEventAccountStock;
    } else {
      const res = await queryBillingEvent({
        variables: {
          event_id: eventId,
          start_date: startDate?.toISOString(),
          end_date: endDate?.toISOString(),
        },
      });

      if (res.data?.getEventStock == null) {
        LayoutStoreInstance.setError(i18n.t('Events:errorGettingInvoiceInfo'));
        return;
      }

      stock = res.data?.getEventStock;
    }

    const date = new Date()
      .toLocaleDateString(i18n.language, DateFormatOptionsForFileNames)
      .replaceAll('/', '-');

    const fileName = sanitizeFilename(
      eventAccountId
        ? `invoice_data_${eventAccount?.name}_${eventName}_${eventId}_${date}.csv`
        : `invoice_data_${eventName}_${eventId}_${date}.csv`,
    );

    const blob = await generateInvoiceInfoReport(
      stock as EventStock | EventAccountStock,
      filterUnusedContainersAndItemLocations,
    );

    download(blob, fileName);
  }

  async function handleOrderFulfillmentDownload(eventId: string) {
    setLoadingOrderfulfillment(true);

    const res = await exportOrderFulfillments({
      variables: {
        filter: { event_id: eventId },
      },
    });

    if (res.error || res.data == null) {
      LayoutStoreInstance.setError(
        i18n.t('Events:errorExportingOrderfulfillments'),
      );

      console.log(res.error);
    } else if (
      res.data.exportEventAccountOrderFulfillments.startsWith('https')
    ) {
      window.open(res.data.exportEventAccountOrderFulfillments, '_blank');
    } else {
      LayoutStoreInstance.setError(
        res.data.exportEventAccountOrderFulfillments,
      );
    }

    setLoadingOrderfulfillment(false);
  }

  async function handleEventOpsOrdersDownload(
    eventId: string,
    eventName: string,
  ) {
    setLoadingEventOpsOrders(true);
    try {
      const res = await queryEventOpsOrders({
        variables: { event_id: eventId },
      });
      const containerTypes =
        res.data?.getAggregatedEventMerchantOrderDataByType?.container_types;
      if (!containerTypes) {
        LayoutStoreInstance.setError(
          i18n.t('Events:errorExportingEventOpsOrders'),
        );
        setLoadingEventOpsOrders(false);
        return;
      }
      if (containerTypes.length === 0) {
        LayoutStoreInstance.setError(i18n.t('Events:noAllEventOpsOrdersFound'));
        setLoadingAllEventOpsOrders(false);
        return;
      }
      const csvData = containerTypes.map((ct) => ({
        container_type_id: ct.item_info.id,
        container_type_name: ct.item_info.name,
        amount_containers_ordered: ct.amount_containers_ordered,
        amount_boxes_ordered: ct.amount_container_stations_ordered,
      }));
      const blob = await toCsvBlob(csvData);
      const date = new Date()
        .toLocaleDateString(i18n.language, DateFormatOptionsForFileNames)
        .replaceAll('/', '-');
      download(
        blob,
        sanitizeFilename(
          `event_ops_orders_${eventName}_${eventId}_${date}.csv`,
        ),
      );
    } catch (e: any) {
      LayoutStoreInstance.setError(
        'Error exporting event ops orders: ' + e.message,
      );
    }
    setLoadingEventOpsOrders(false);
  }

  async function handleAllEventOpsOrdersDownload(
    eventId: string,
    eventName: string,
  ) {
    setLoadingAllEventOpsOrders(true);
    try {
      const res = await queryAllEventOpsOrders({
        variables: { eventId },
      });
      const sharedLinks =
        res.data?.getMerchantOrderSharedLinksForEvent
          ?.eventMerchantorderSharedLinks;
      if (!sharedLinks) {
        LayoutStoreInstance.setError(
          i18n.t('Events:errorExportingAllEventOpsOrders'),
        );
        setLoadingAllEventOpsOrders(false);
        return;
      }
      if (sharedLinks.length === 0) {
        LayoutStoreInstance.setError(i18n.t('Events:noAllEventOpsOrdersFound'));
        setLoadingAllEventOpsOrders(false);
        return;
      }

      // Collect all unique container types across all orders
      const containerTypeMap = new Map<
        number,
        { id: number; name: string; packing_unit: number | null }
      >();
      sharedLinks.forEach((link) => {
        link.merchant_order?.latest_revision?.containers.forEach((item) => {
          if (item.container_type && !containerTypeMap.has(item.type_id)) {
            containerTypeMap.set(item.type_id, {
              id: item.container_type.id,
              name: item.container_type.name,
              packing_unit: item.container_type.packing_unit ?? null,
            });
          }
        });
      });

      // Sort container types by ID for consistent column ordering
      const containerTypes = Array.from(containerTypeMap.values()).sort(
        (a, b) => a.id - b.id,
      );

      // Build CSV data
      const csvData = sharedLinks.map((link) => {
        const status = link.merchant_order?.status;
        const baseRow: Record<string, string | number | null> = {
          event_account_id: link.event_account?.id ?? '',
          event_account_name: link.event_account?.name ?? '',
          ops_order_status: status
            ? getPresetKeyForStatus(status, 'admin')
            : 'order_open',
          sales_order_url: link.sales_order_url ?? '',
          comment: link.merchant_order?.comment ?? '',
        };

        // Add dynamic container type columns
        containerTypes.forEach((ct) => {
          const item = link.merchant_order?.latest_revision?.containers.find(
            (i) => i.type_id === ct.id,
          );
          const amountBoxes = item?.amount_int ?? 0;
          const packingUnit = ct.packing_unit ?? 0;
          const total = amountBoxes * packingUnit;

          const prefix = `${ct.id}_${ct.name}`;
          baseRow[`${prefix}_amount_boxes_ordered`] = amountBoxes;
          baseRow[`${prefix}_packaging_unit`] = packingUnit;
          baseRow[`${prefix}_ordered_containers_total`] = total;
        });

        return baseRow;
      });

      const blob = await toCsvBlob(csvData);
      const date = new Date()
        .toLocaleDateString(i18n.language, DateFormatOptionsForFileNames)
        .replaceAll('/', '-');
      download(
        blob,
        sanitizeFilename(
          `all_event_ops_orders_${eventName}_${eventId}_${date}.csv`,
        ),
      );
    } catch (e: any) {
      LayoutStoreInstance.setError(
        'Error exporting all event ops orders: ' + e.message,
      );
    }
    setLoadingAllEventOpsOrders(false);
  }

  const getAuthorizedSignersAndName = () => {
    const queryEventAccountOnly = !!eventAccount;
    const eventId = event.id;

    let eventAccounts = [];
    if (queryEventAccountOnly) {
      eventAccounts.push(eventAccount);
    } else {
      eventAccounts = [...(event.event_participants || [])];
    }

    eventAccounts = eventAccounts
      .flatMap((eA) => {
        const { authorized_signers, item_locations } = eA;

        // Collect all unique allowed item location IDs
        const allowedItemLocationIdSet = new Set(
          authorized_signers.flatMap((signer) =>
            signer?.allowed_item_locations
              ?.filter((signer) => signer.event_id === eventId)
              ?.map((ail) => ail.id),
          ),
        );

        // Filter item locations based on allowed IDs and event ID
        const validLocationIds = new Set(
          item_locations
            ?.filter(
              ({ id, event }) =>
                allowedItemLocationIdSet.has(id) &&
                (event?.id == null || event?.id === eventId),
            )
            .map((loc) => loc.id),
        );

        // Filter signers who are allowed at valid locations
        const signers = authorized_signers.filter((signer) =>
          signer.allowed_item_locations?.some((ail) =>
            validLocationIds.has(ail.id),
          ),
        );

        return {
          ...eA,
          authorized_signers: signers,
        };
      })
      .filter((eA) => eA.authorized_signers.length > 0);

    const authorizedSigners = eventAccounts.flatMap(
      (eA) => eA?.authorized_signers,
    );

    const name = queryEventAccountOnly ? eventAccount?.name : event.name;

    return { authorizedSigners, name };
  };

  async function downloadAuthorizedSignerCsv() {
    setLoadingAuthorizedSignerCSV(true);

    const { authorizedSigners, name } = getAuthorizedSignersAndName();

    if (!authorizedSigners) {
      setLoadingAuthorizedSignerCSV(false);
      LayoutStoreInstance.setError(i18n.t('Common:error'));
      return;
    }

    const csvObject = authorizedSigners.map((signer) => ({
      qrCode: `${AUTHORIZED_SIGNER_QR_CODE_PREFIX}${signer.id}`,
      name: signer.name,
      id: signer.id,
    }));

    const csvBlob = await toCsvBlob(csvObject);
    download(csvBlob, `authorized_signers_${name}.csv`);

    setLoadingAuthorizedSignerCSV(false);
  }

  async function downloadAuthorizedSignerPdf() {
    setLoadingAuthorizedSignerPDF(true);

    const { authorizedSigners, name } = getAuthorizedSignersAndName();

    await DownloadAuthorizedSignerPdf(
      authorizedSigners || [],
      name || undefined,
    );

    setLoadingAuthorizedSignerPDF(false);
  }

  async function downloadMultiAuthorizedSignerPdfs() {
    setLoadingMultiAuthorizedSignerPDF(true);

    const { authorizedSigners } = getAuthorizedSignersAndName();

    (authorizedSigners || []).forEach(async (signer) => {
      await DownloadAuthorizedSignerPdf([signer], signer?.name || undefined);
    });

    setLoadingMultiAuthorizedSignerPDF(false);
  }

  return (
    <>
      <div className="d-flex flex-column">
        {showDeposit && (
          <Button
            className="mb-2 text-start"
            onClick={() =>
              openDateRangePickerModal(
                event.id,
                event.name || 'Event',
                'deposit_clearing',
              )
            }
            color="secondary"
            disabled={loadingDepositClearing}
          >
            <FaDownload /> {i18n.t('Events:downloadDepositClearing')}
          </Button>
        )}

        {showInvoice && (
          <Button
            className="mb-2 text-start"
            onClick={() =>
              openDateRangePickerModal(
                event.id,
                event.name || 'Event',
                'invoice_info',
              )
            }
            color="secondary"
            disabled={loadingInvoice}
          >
            <FaDownload /> {i18n.t('Events:downloadInvoiceInfo')}
          </Button>
        )}

        {showOrderfulfillment && (
          <Button
            className="mb-2 text-start"
            onClick={async () => {
              await handleOrderFulfillmentDownload(event.id);
            }}
            color="secondary"
            disabled={loadingOrderfulfillment}
          >
            <FaDownload /> {i18n.t('Events:downloadOrderfulfillments')}
          </Button>
        )}

        {showEventOpsOrders && (
          <Button
            className="mb-2 text-start"
            onClick={() =>
              handleEventOpsOrdersDownload(event.id, event.name || 'Event')
            }
            color="secondary"
            disabled={loadingEventOpsOrders}
          >
            <FaFileCsv /> {i18n.t('Events:downloadEventOpsOrders')}
          </Button>
        )}

        {showAllEventOpsOrders && (
          <Button
            className="mb-2 text-start"
            onClick={() =>
              handleAllEventOpsOrdersDownload(event.id, event.name || 'Event')
            }
            color="secondary"
            disabled={loadingAllEventOpsOrders}
          >
            <FaFileCsv /> {i18n.t('Events:downloadAllEventOpsOrders')}
          </Button>
        )}

        {showAuthorizedSigner && (
          <Button
            className="mb-2 text-start"
            onClick={async () => {
              await downloadAuthorizedSignerCsv();
            }}
            color="secondary"
            disabled={loadingAuthorizedSignerCSV}
          >
            <FaFileCsv /> {i18n.t('Events:downloadAuthorizedSignerCSV')}
          </Button>
        )}
        {showAuthorizedSigner && (
          <Button
            className="mb-2 text-start"
            onClick={async () => {
              await downloadAuthorizedSignerPdf();
            }}
            color="secondary"
            disabled={loadingAuthorizedSignerPDF}
          >
            <FaFilePdf /> {i18n.t('Events:downloadAuthorizedSignerPDF')}
          </Button>
        )}
        {showAuthorizedSigner && (
          <Button
            className="mb-2 text-start"
            onClick={async () => {
              await downloadMultiAuthorizedSignerPdfs();
            }}
            color="secondary"
            disabled={loadingMultiAuthorizedSignerPDF}
          >
            <FaFilePdf /> {i18n.t('Events:downloadMultiAuthorizedSignerPDF')} (
            {getAuthorizedSignersAndName().authorizedSigners?.length || 0})
          </Button>
        )}
      </div>

      {isInvoiceDownloadModalOpen && (
        <Modal
          isOpen={isInvoiceDownloadModalOpen}
          onClosed={closeDateRangePickerModal}
        >
          <ModalHeader>{i18n.t('Common:download')}</ModalHeader>

          <ModalBody>
            <h4>{i18n.t('Common:startDate')}:</h4>
            <DateTimePicker dateTime={startDate} setDateTime={setStartDate} />
            <h4>{i18n.t('Common:endDate')}:</h4>
            <DateTimePicker dateTime={endDate} setDateTime={setEventEndDate} />

            {reportType === 'invoice_info' && (
              <FormGroup check className="mt-3">
                <Label check>
                  <Input
                    type="checkbox"
                    checked={filterUnusedContainersAndItemLocations}
                    onChange={(e) =>
                      setFilterUnusedContainersAndItemLocations(
                        e.target.checked,
                      )
                    }
                  />{' '}
                  {i18n.t('Events:doNotIncludeUnusedContainerTypes')}
                </Label>
              </FormGroup>
            )}

            <div className="d-flex justify-content-end">
              <Button
                className="mb-2 ms-2 mt-2"
                color="primary"
                onClick={() => {
                  onDateRangePickerDownloadClick(startDate, endDate);
                }}
              >
                {i18n.t('Common:download')}
              </Button>
              <Button
                className="mb-2 ms-2 mt-2"
                color="secondary"
                onClick={closeDateRangePickerModal}
              >
                {i18n.t('Common:cancel')}
              </Button>
            </div>
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default DownloadsModal;
